import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Checkbox, Dialog, SearchField } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { addListContacts, useContactSearch } from '../api';
import styles from './lists.module.css';

/** „Kontakte hinzufügen" of a manual list (Terminierung 06): search the contacts, tick several, add them. */
export function AddContactsDialog({
  open,
  listId,
  onClose,
  onAdded,
}: {
  open: boolean;
  listId: string;
  onClose: () => void;
  onAdded: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const search = useContactSearch(debounced, open && debounced.trim().length >= 2);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(text), 250);
    return () => window.clearTimeout(id);
  }, [text]);

  function close() {
    setText('');
    setDebounced('');
    setSelected(new Map());
    setError(null);
    onClose();
  }

  async function save() {
    if (selected.size === 0) return;
    setPending(true);
    setError(null);
    try {
      const result = await addListContacts(listId, [...selected.keys()]);
      await queryClient.invalidateQueries({ queryKey: [...queryRoots.dialer] });
      const parts = [`${result.added} hinzugefügt`];
      if (result.alreadyInList > 0) parts.push(`${result.alreadyInList} schon in der Liste`);
      if (result.blocked > 0) parts.push(`${result.blocked} gesperrt (DNC)`);
      onAdded(`Kontakte: ${parts.join(' · ')}.`);
      close();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  const items = search.data?.items ?? [];
  return (
    <Dialog
      open={open}
      onClose={close}
      eyebrow="Manuelle Liste"
      title="Kontakte hinzufügen"
      width={600}
      footerNote={
        selected.size > 0 ? <span className={styles.selectedNote}>{selected.size} ausgewählt</span> : null
      }
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Abbrechen
          </Button>
          <Button loading={pending} disabled={selected.size === 0} onClick={() => void save()}>
            Hinzufügen
          </Button>
        </>
      }
    >
      <div className={styles.dialogBody}>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <SearchField
          label="Kontakt suchen"
          placeholder="Name, Telefon oder E-Mail"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <div className={styles.searchResults} role="group" aria-label="Treffer">
          {debounced.trim().length < 2 ? (
            <div className={styles.searchStatus}>Mindestens zwei Zeichen eingeben.</div>
          ) : search.isPending ? (
            <div className={styles.searchStatus}>Lädt …</div>
          ) : items.length === 0 ? (
            <div className={styles.searchStatus}>Kein Kontakt gefunden.</div>
          ) : (
            items.map((item) => {
              const name = `${item.firstName} ${item.lastName}`.trim();
              return (
                <Checkbox
                  key={item.id}
                  label={name}
                  description={item.dnc ? `${item.lifecycle.label} · gesperrt (DNC)` : item.lifecycle.label}
                  checked={selected.has(item.id)}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setSelected((current) => {
                      const next = new Map(current);
                      if (checked) next.set(item.id, name);
                      else next.delete(item.id);
                      return next;
                    });
                  }}
                />
              );
            })
          )}
        </div>
      </div>
    </Dialog>
  );
}
